import React, { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrid,
  faCalendarDays,
  faMessageLines,
  faAddressBook,
  faCircleEllipsis,
  faMessageQuestion,
  faStar,
  faPhone,
} from "@fortawesome/pro-light-svg-icons";

import { faTabletScreenButton, faCircleCheck } from "@fortawesome/pro-regular-svg-icons";

import { Box, Menu, MenuItem, ThemeProvider, Typography } from "@mui/material";

import BottomNavigation from "@mui/material/BottomNavigation";
import BottomNavigationAction from "@mui/material/BottomNavigationAction";
import "./mobile-footer.sass";
import { NotificationDot } from "../NotificationDot";
import { useGetContacts } from "@/routes/new-contacts/hooks";
import { SafeListMode } from "@/openapi";
import ChildContext from "@/context/child";
import { useIsWatchChild, useWhatsNewState, useIsPinwheelHomeChild } from "@/hooks";
import { typographyTheme } from "@/styles/customTheme";
import PinwheelAskIcon from "@/assets/icons/pinwheel-ask-icon.svg?react";
import { useContactPermissions, usePhoneHistoryPermissions, usePinwheelAskPermissions, useAppsScheduleModesRoutinesPermissions } from "@/util/permission";
import { useTranslation } from "react-i18next";

interface MenuItemProps {
  icon: React.ReactElement;
  name: string;
  path?: string;
  paths?: string[];
  isModal?: boolean;
  dataCY?: string;
  onClick?: () => void;
  showNotificationDot?: boolean;
  hide?: boolean;
}

const Navigation = () => {
  const { child } = useContext(ChildContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [activeMenuItem, setActiveMenuItem] = useState(location.pathname);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const mobileFooterRef = useRef<HTMLDivElement | null>(null);
  const { pendingContacts } = useGetContacts();
  const isWatchChild = useIsWatchChild();
  const isPinwheelHomeChild = useIsPinwheelHomeChild(child);
  const { showNotification, onLinkClicked } = useWhatsNewState();
  const { noAccess: hasNoContactAccess } = useContactPermissions();
  const { noAccess: hasNoPinwheelAskAccess } = usePinwheelAskPermissions();
  const { noAccess: hasNoAppAccess } = useAppsScheduleModesRoutinesPermissions();
  const { noAccess: hasNoPhoneHistoryAccess } = usePhoneHistoryPermissions();
  const { t } = useTranslation("common");

  const menuItems: MenuItemProps[] = [
    {
      icon: <FontAwesomeIcon icon={faGrid} size="2x" />,
      name: t("dashboard"),
      path: "/dashboard",
      hide: isPinwheelHomeChild,
    },
    {
      icon: <FontAwesomeIcon icon={faCalendarDays} size="2x" />,
      name: t("schedule"),
      path: "/calendar",
      dataCY: "mobileNavSchedule",
      hide: hasNoAppAccess,
    },
    {
      icon: <FontAwesomeIcon icon={isPinwheelHomeChild ? faPhone : faMessageLines} size="2x" />,
      name: t("history"),
      path: "/phone-history",
      dataCY: "mobileNavPhoneHistory",
      hide: hasNoPhoneHistoryAccess,
    },
    {
      icon: (
        <Box
          sx={{
            display: "block",
            position: "relative",
          }}
        >
          <FontAwesomeIcon icon={faAddressBook} size="2x" />
          {child?.configuration.safelistMode === SafeListMode.Approved && pendingContacts.length > 0 ? (
            <NotificationDot
              sx={{
                position: "absolute",
                top: "-5px",
                right: "-8px",
                fontSize: pendingContacts.length > 9 ? "10px!important" : "12px",
              }}
            >
              {pendingContacts.length > 9 ? "9+" : pendingContacts.length}
            </NotificationDot>
          ) : null}
        </Box>
      ),
      name: t("contacts"),
      path: "/contacts",
      dataCY: "mobileNavContacts",
      hide: hasNoContactAccess
    },
    {
      icon: (
        <Box
          sx={{
            display: "block",
            position: "relative",
          }}
        >
          <FontAwesomeIcon icon={faCircleEllipsis} size="2x" />
          {/* TODO: put the menu in a hook.  Separate out the first 5 menu items.
        Calculate the overflow off of the menu items in the overflow that show notifications rather than hardcoding */}
          {showNotification ? (
            <NotificationDot
              sx={{
                position: "absolute",
                top: "-5px",
                right: "-8px",
              }}
            />
          ) : null}
        </Box>
      ),
      name: t("more"),
      paths: ["/apps", "/routines"],
      isModal: true,
      dataCY: "mobileNavMore",
    },
    {
      icon: <PinwheelAskIcon />,
      name: "PinwheelAsk",
      path: "/pinwheel-gpt",
      isModal: true,
      dataCY: "mobileNavPinwheelAsk",
      hide: hasNoPinwheelAskAccess || isPinwheelHomeChild
    },
    {
      icon: <FontAwesomeIcon icon={faTabletScreenButton} size="lg" />,
      name: t("apps"),
      path: "/apps",
      isModal: true,
      dataCY: "mobileNavApps",
      hide: hasNoAppAccess || isPinwheelHomeChild,
    },
    {
      icon: <FontAwesomeIcon icon={faCircleCheck} size="lg" />,
      name: t("routines", { ns: "routine"}),
      path: "/routines",
      isModal: true,
      dataCY: "mobileNavRoutines",
      hide: hasNoAppAccess || isPinwheelHomeChild,
    },
    {
      icon: <FontAwesomeIcon icon={faMessageQuestion} size="lg" />,
      name: t("help"),
      onClick: () => {
        window.open("https://support.pinwheel.com", "_blank");
      },
      isModal: true,
    },
    {
      icon: <FontAwesomeIcon icon={faStar} size="lg" />,
      name: t("whatsNew"),
      onClick: () => {
        onLinkClicked();
        window.open("https://www.pinwheel.com/whats-new", "_blank");
      },
      showNotificationDot: showNotification,
      isModal: true,
    },
  ];

  useEffect(() => {
    if (location.pathname) {
      setActiveMenuItem(location.pathname);
    }
  }, [location.pathname]);

  const clickMenuItem = (e: React.MouseEvent<HTMLElement>, link: MenuItemProps) => {
    if (link.path) {
      setActiveMenuItem(link.path);
      navigate(link.path);
    }
    if (link.onClick) {
      link.onClick();
    }
    if (link.isModal) {
      handleClick(e);
    }
  };

  // handle morebutton click for floating menu
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    if (mobileFooterRef.current) {
      mobileFooterRef.current.addEventListener("touchmove", (e) => {
        e.preventDefault();
      });
    }
  }, []);

  return (
    <ThemeProvider theme={typographyTheme}>
      <div className="mobile-footer-container" ref={mobileFooterRef}>
        <nav className="mobile-footer" role="navigation" aria-label="main navigation">
          <BottomNavigation showLabels className="mobile-navbar">
            {menuItems.slice(0, 5).map((link, index) => {
              if (link.hide) return null; // Skip rendering if hide is true
              return (
                <BottomNavigationAction
                  onClick={(e) => {
                    clickMenuItem(e, link);
                  }}
                  key={index}
                  className={`${
                    (link.path && activeMenuItem.includes(link.path)) ||
                    (link.paths && link.paths.some((path) => activeMenuItem.includes(path)))
                      ? "active"
                      : ""
                  }`}
                  label={<Typography variant="caption">{link.name}</Typography>}
                  icon={link.icon}
                  data-cy={link.dataCY}
                />
              );
            })}
          </BottomNavigation>
          <Menu
            anchorEl={anchorEl}
            id="account-menu"
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            onClick={handleMenuClose}
            sx={{
              "& .MuiBackdrop-root": {
                bottom: "3.5rem",
              },
            }}
            slotProps={{
              paper: {
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                  mb: 2,
                  top: "auto !important",
                  bottom: 60,
                  px: 1.5,
                  py: 0.7,
                },
              }
            }}
            transformOrigin={{ horizontal: "right", vertical: "top" }}
            anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
          >
            {menuItems.slice(5, 11).map((link, index) => {
              if (link.hide) return null;
              if (isWatchChild && link.path === "/routines") return null;
              return (
                <MenuItem
                  key={index}
                  onClick={(e) => clickMenuItem(e, link)}
                  data-cy={link.dataCY}
                  sx={{ alignItems: "center", "& > .menuIcon": { width: "20px" }, my: .5 }}
                >
                  <span className="menuIcon">{link.icon}</span><Typography variant="body1" ml={1.5} pr={4}>{link.name}</Typography>{link.showNotificationDot ? <NotificationDot/> : null}
                </MenuItem>
              );
            })}
          </Menu>
        </nav>
      </div>
    </ThemeProvider>
  );
};
export default Navigation;
